<template>
  <div class="tc-app-shell">
    <AppHeader>
      <!-- Identity menu — timezone, admin, sign out live inside -->
      <IdentityMenu
        :display-name="displayName"
        :super-admin="superAdmin"
        :tenant-admin="tenantAdmin"
        :signing-out="signingOut"
        @sign-out="handleSignOut"
      />
    </AppHeader>

    <div
      v-if="savedFlash"
      class="alert alert-success mb-0 rounded-0 text-center py-2"
      role="status"
      data-testid="home-saved-flash"
    >{{ savedFlash }}</div>

    <DraftTriagePanel
      :events-open="eventsOpen"
      :just-saved="savedFlash !== null"
      @toggle-events="eventsOpen = !eventsOpen"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import AppHeader from '../components/AppHeader.vue';
import DraftTriagePanel from '../components/DraftTriagePanel.vue';
import IdentityMenu from '../components/IdentityMenu.vue';
import { getAuthMe, signOut as apiSignOut, ApiClientError } from '../api/client';
import { useTriageStore } from '../stores/triage';

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const triageStore = useTriageStore();

const signingOut = ref(false);
const superAdmin = ref(false);
const tenantAdmin = ref(false);
const displayName = ref('');
const eventsOpen = ref(false);
const savedFlash = ref<string | null>(null);

// ---------------------------------------------------------------------------
// Identity load
// ---------------------------------------------------------------------------
async function loadIdentity(): Promise<void> {
  try {
    const me = await getAuthMe();
    superAdmin.value = me.superAdmin;
    tenantAdmin.value = me.tenantAdmin;
    displayName.value = me.displayName;
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 401) {
      await router.replace({ path: '/login', query: { flash: 'Please sign in.' } });
    }
  }
}

onMounted(() => {
  void loadIdentity();
  // Confirm a just-completed VP write — Verify redirects here with ?saved=<count>
  // (its own success flash is lost the moment it navigates away). Strip the query
  // afterwards so a refresh doesn't re-show the message.
  const saved = route.query.saved;
  if (typeof saved === 'string' && saved) {
    const n = Number(saved);
    savedFlash.value = Number.isFinite(n) && n > 0
      ? `Published ${n} ${n === 1 ? 'entry' : 'entries'} to Vantagepoint.`
      : 'Published to Vantagepoint.';
    setTimeout(() => { savedFlash.value = null; }, 5000);
    void router.replace({ path: '/home' });
  }
});

async function handleSignOut(): Promise<void> {
  signingOut.value = true;
  try {
    try {
      await apiSignOut();
    } catch {
      // Sign-out endpoint error — clear local state and redirect anyway.
    }

    queryClient.clear();
    triageStore.clear();
    await router.replace('/login');
  } finally {
    signingOut.value = false;
  }
}
</script>
